/**
 * Configuration for iframe embedding on external domains
 */

// List of domains that should be treated as iframe-capable
const IFRAME_CAPABLE_DOMAINS = [
	"appv.ceruleo.io",
	"appbuilder.ceruleo.io", // if you set this up
	"app-builder.ceruleo.io", // alternative
	"ceruleo.io",
	// Add more domains as needed
];

/**
 * Check if current domain should attempt iframe embedding
 */
export function shouldAttemptIframeEmbedding(): boolean {
	const hostname = window.location.hostname;

	// Always allow ShapeDiver domains
	if (hostname.includes("shapediver.com")) {
		return true;
	}

	// Check if current domain is in our allowlist
	return IFRAME_CAPABLE_DOMAINS.includes(hostname);
}

/**
 * Get iframe embedding instructions for current domain
 */
export function getIframeEmbeddingInstructions(): string {
	const hostname = window.location.hostname;

	return `
## Setting Up Iframe Embedding for ${hostname}

### 🔧 **Required Steps:**

#### 1. Contact ShapeDiver Support
- **Email**: [support@shapediver.com](mailto:support@shapediver.com)
- **Request**: Domain whitelisting for iframe embedding
- **Specify Domain**: \`${hostname}\`
- **Purpose**: External App Builder deployment

#### 2. Model Configuration
In your ShapeDiver model settings:
- ✅ Enable **"Iframe Embedding"** 
- ✅ Add \`${hostname}\` to allowed domains
- ✅ Configure **"Embedding Settings"** for your organization

#### 3. Verification
Once configured, your URLs will work:
\`\`\`
${window.location.origin}/?slug=your-model-slug
\`\`\`

### 🔄 **Alternative: Use Direct Embedding**

While waiting for domain approval, use direct embedding:
\`\`\`
${window.location.origin}/?ticket=YOUR_TICKET&modelViewUrl=YOUR_MODEL_VIEW_URL
\`\`\`

**Get ticket/modelViewUrl from:**
1. Your model → Developer Settings
2. Enable "Direct Embedding"  
3. Copy "Embedding Ticket" and "Model View URL"

### 📞 **Need Help?**
- [ShapeDiver Help Center](https://help.shapediver.com/doc/iframe-settings)
- [Embedding Documentation](https://help.shapediver.com/doc/setup-domains-for-embedding)
`;
}

/**
 * Enhanced error message for iframe embedding failures
 */
export function getIframeErrorGuidance(error: any): string {
	const hostname = window.location.hostname;

	return `
## Iframe Embedding Failed

**Error**: ${error.message || "CORS policy blocked the request"}

### 🚨 **Root Cause**
The domain \`${hostname}\` is not authorized for iframe embedding with ShapeDiver.

### ✅ **Solution**
${getIframeEmbeddingInstructions()}

### ⚡ **Quick Workaround**
Use direct embedding instead of slug-based URLs:

**Instead of:**
\`?slug=react-ar-cube\`

**Use:**
\`?ticket=YOUR_TICKET&modelViewUrl=YOUR_MODEL_VIEW_URL\`

This bypasses the iframe embedding restrictions.
`;
}
